import { FC } from "react";
import { Badge, Text, Button, Group } from "@mantine/core";
import { useStyles } from "../Regtank.styles";

/**
 * Reusable cell components for column definitions
 * Extracted to reduce duplication across multiple column files
 */

interface ReattemptApprovalBadgeCellProps {
  label: string | undefined;
  getReattemptApprovalColor: (approval: string) => string | undefined;
}

/**
 * Cell component for rendering reattempt approval status as a badge
 * Handles empty string and "-" sentinel values
 */
export const ReattemptApprovalBadgeCell: FC<ReattemptApprovalBadgeCellProps> = ({
  label,
  getReattemptApprovalColor,
}) => {
  if (label === "") return null;
  if (label === "-") return <>{label}</>;

  return (
    <Badge color={getReattemptApprovalColor(label || "")}>
      {label}
    </Badge>
  );
};

interface FailureReasonCellProps {
  reasons: string[];
}

/**
 * Cell component for rendering failure reasons as text or list
 * Handles array display with conditional formatting
 */
export const FailureReasonCell: FC<FailureReasonCellProps> = ({ reasons }) => {
  const { classes } = useStyles();

  if (!Array.isArray(reasons) || reasons.length === 0) return null;

  // Display as plain text if single item or starts with "-"
  if (reasons[0]?.startsWith("-") || reasons.length < 2) {
    return <Text>{reasons}</Text>;
  }

  // Display as list for multiple reasons
  return (
    <ul className={classes.reasonList}>
      {reasons.map((reason, index) => (
        <li className={classes.reasonItem} key={index}>
          {reason}
        </li>
      ))}
    </ul>
  );
};

interface DocumentViewButtonGroupProps {
  disableFront: boolean;
  disableBack: boolean;
  onViewFront: () => void;
  onViewBack: () => void;
}

/**
 * Button group for viewing front and back document photos
 * Used in onboarding verification history
 */
export const DocumentViewButtonGroup: FC<DocumentViewButtonGroupProps> = ({
  disableFront,
  disableBack,
  onViewFront,
  onViewBack,
}) => (
  <Group>
    <Button disabled={disableFront} onClick={onViewFront}>
      VIEW FRONT PHOTO
    </Button>
    <Button disabled={disableBack} onClick={onViewBack}>
      VIEW BACK PHOTO
    </Button>
  </Group>
);

interface PhotoVideoButtonGroupProps {
  disabled: boolean;
  onViewPhoto: () => void;
  onViewVideo: () => void;
}

/**
 * Button group for viewing liveness check photo and video
 * Used in both onboarding and liveness verification history
 */
export const PhotoVideoButtonGroup: FC<PhotoVideoButtonGroupProps> = ({
  disabled,
  onViewPhoto,
  onViewVideo,
}) => (
  <Group>
    <Button disabled={disabled} onClick={onViewPhoto}>
      VIEW PHOTO
    </Button>
    <Button disabled={disabled} onClick={onViewVideo}>
      VIEW VIDEO
    </Button>
  </Group>
);
